import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import { formatCurrency } from '~/utils';
import { shipFromFacilityLabel } from '~/utils/facilityOptions';
import {
  QuoteStatus,
  type Currency,
  type GetRequestForQuoteWithQuotesQuery,
} from '~/graphql/graphql';
import type { ItemRow, ProductGroup } from '~/pages/rfq-view/itemGroups';
import {
  ProductGroupTable,
  ProductGroupCells,
} from '~/pages/rfq-view/ProductGroupTable';
import {
  itemsByPlatformProductLengthId,
  moneyOrDash,
  offeredBundleQuantity,
} from '~/pages/rfq-view/sections/quoteDisplay';
import { QuoteResponseForm } from '~/pages/quote-response/QuoteResponseForm';
import type { QuoteResponseSubmission } from '~/pages/quote-response/useQuoteResponseDraft';
import {
  sellerResponseSeed,
  shipFromFacilitiesOf,
} from '~/pages/rfq-view/sellerResponseSeed';
import { useSaveQuoteResponse } from '~/pages/rfq-view/useSaveQuoteResponse';

export type QuoteCardQuote = Omit<
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote'][number],
  'recipients' | 'lockedRecipientIds' | 'rounds'
>;
export type QuoteCardItem = QuoteCardQuote['items'][number];

export type QuoteCardProps = Readonly<{
  quote: QuoteCardQuote;
  groups: ProductGroup[];
  isRequestForQuoteClosed: boolean;
  requestForQuoteUsesTallyPrice: boolean;
  requestForQuoteHasCannedTally: boolean;
  requestForQuoteCurrency: Currency | null | undefined;
  footer?: ReactNode;
  statusLabel?: ReactNode;
}>;

function quoteStatusTone(status: QuoteStatus): StatusBadgeTone {
  switch (status) {
    case QuoteStatus.Accepted:
      return 'accepted';
    case QuoteStatus.Submitted:
      return 'shipped';
    case QuoteStatus.Archived:
      return 'neutral';
    default:
      return 'pending';
  }
}

export function QuoteCardShell({
  quote,
  bodyExtra,
  rows,
  footer,
  statusLabel,
}: Readonly<{
  quote: QuoteCardQuote;
  bodyExtra: ReactNode;
  rows: ReactNode;
  footer?: ReactNode;
  statusLabel?: ReactNode;
}>) {
  const { t } = useTranslation('rfq-view');

  return (
    <>
      {bodyExtra}

      {quote.leadTimeDays != null && (
        <p className="text-sm text-gray-600 mb-3">
          <span className="font-semibold">
            {t('rfq_view.lead_time_label')}:
          </span>{' '}
          {t('rfq_view.lead_time_days', { count: quote.leadTimeDays })}
        </p>
      )}

      <SectionPanel
        title={t('rfq_view.requested_products_heading')}
        aside={
          <span
            className={`${statusBadgeClasses(quoteStatusTone(quote.status))} font-semibold`}
          >
            {statusLabel ?? t(`rfq_view.quote_status.${quote.status}`)}
          </span>
        }
      >
        {rows}
      </SectionPanel>

      {footer && quote.status !== QuoteStatus.Accepted && (
        <div className="flex justify-end gap-3 mt-4">{footer}</div>
      )}
    </>
  );
}

export type ReadOnlyQuoteCardProps = QuoteCardProps &
  Readonly<{
    selectionColumnHeader?: ReactNode;
    renderSelectionCell?: (
      item: QuoteCardItem | undefined,
      row: ItemRow
    ) => ReactNode;
  }>;

export function ReadOnlyQuoteCard({
  quote,
  groups,
  requestForQuoteUsesTallyPrice,
  footer,
  statusLabel,
  selectionColumnHeader,
  renderSelectionCell,
}: ReadOnlyQuoteCardProps) {
  const { t } = useTranslation('rfq-view');

  const itemByPlatformProductLengthId = itemsByPlatformProductLengthId(
    quote.items
  );

  const sellingOrganizationFacility = quote.sellingOrganizationFacility;

  const bodyExtra = (
    <>
      {sellingOrganizationFacility && (
        <p className="text-sm text-gray-600 mb-1">
          <span className="font-semibold">
            {t('rfq_view.ship_from_label')}:
          </span>{' '}
          {shipFromFacilityLabel(
            sellingOrganizationFacility,
            quote.sellingOrganization.facilities
          )}
        </p>
      )}

      {(quote.offeredPickupPriceForTally ||
        quote.offeredDeliveryPriceForTally) && (
        <p className="text-sm text-gray-600 mb-3">
          {quote.offeredPickupPriceForTally && (
            <>
              <span className="font-semibold">
                {t('rfq_view.tally_pickup_price_label')}:
              </span>{' '}
              {formatCurrency(
                quote.offeredPickupPriceForTally.amount,
                quote.offeredPickupPriceForTally.currency
              )}
            </>
          )}
          {quote.offeredPickupPriceForTally &&
            quote.offeredDeliveryPriceForTally &&
            '  ·  '}
          {quote.offeredDeliveryPriceForTally && (
            <>
              <span className="font-semibold">
                {t('rfq_view.tally_delivery_price_label')}:
              </span>{' '}
              {formatCurrency(
                quote.offeredDeliveryPriceForTally.amount,
                quote.offeredDeliveryPriceForTally.currency
              )}
            </>
          )}
        </p>
      )}
    </>
  );

  const rows = (
    <ProductGroupTable
      groups={groups}
      columnLabels={
        requestForQuoteUsesTallyPrice
          ? [t('rfq_view.quantity_label')]
          : [
              t('rfq_view.quantity_label'),
              t('rfq_view.pickup_price_label'),
              t('rfq_view.delivery_price_label'),
            ]
      }
      leadingColumnHeader={selectionColumnHeader}
      renderRow={(row) => {
        const item = itemByPlatformProductLengthId.get(
          row.platformProductLengthId
        );

        return (
          <ProductGroupCells
            label={row.displayValue}
            leading={
              renderSelectionCell && <>{renderSelectionCell(item, row)}</>
            }
            values={[
              {
                columnKey: 'quantity',
                value: item ? offeredBundleQuantity(item, row) : '—',
              },
              ...(requestForQuoteUsesTallyPrice
                ? []
                : [
                    {
                      columnKey: 'pickupPrice',
                      value: item ? moneyOrDash(item.offeredPickupPrice) : '—',
                    },
                    {
                      columnKey: 'deliveryPrice',
                      value: item
                        ? moneyOrDash(item.offeredDeliveryPrice)
                        : '—',
                    },
                  ]),
            ]}
          />
        );
      }}
    />
  );

  return (
    <QuoteCardShell
      quote={quote}
      bodyExtra={bodyExtra}
      rows={rows}
      footer={footer}
      statusLabel={statusLabel}
    />
  );
}

export type QuoteEditFormProps = Readonly<{
  quote: QuoteCardQuote;
  groups: ProductGroup[];
  isRequestForQuoteClosed?: boolean;
  requestForQuoteUsesTallyPrice: boolean;
  requestForQuoteHasCannedTally: boolean;
  requestForQuoteCurrency: Currency | null | undefined;
  linkedOrganizationId?: string;
  onSubmitted?: () => void;
}>;

export function QuoteEditForm({
  quote,
  groups,
  isRequestForQuoteClosed = false,
  requestForQuoteUsesTallyPrice,
  requestForQuoteHasCannedTally,
  requestForQuoteCurrency,
  linkedOrganizationId,
  onSubmitted,
}: QuoteEditFormProps) {
  const { t } = useTranslation('rfq-view');
  const { save, isSaving } = useSaveQuoteResponse();
  const facilities = useMemo(() => shipFromFacilitiesOf(quote), [quote]);
  const seed = useMemo(
    () => sellerResponseSeed(quote, groups, requestForQuoteCurrency),
    [groups, quote, requestForQuoteCurrency]
  );

  const handleSave = async (submission: QuoteResponseSubmission) => {
    const isSaved = await save(
      quote.id,
      requestForQuoteUsesTallyPrice,
      submission
    );
    if (isSaved && submission.submit) {
      onSubmitted?.();
    }
  };

  return (
    <QuoteResponseForm
      quoteId={quote.id}
      seed={seed}
      facilities={facilities}
      submitting={isSaving}
      disabled={isRequestForQuoteClosed}
      hasFixedLines={requestForQuoteHasCannedTally}
      usesWholeRequestPrice={requestForQuoteUsesTallyPrice}
      onSubmit={(submission) => void handleSave(submission)}
      onSaveDraft={(submission) => void handleSave(submission)}
      onInvalid={(errorKey) => toast.error(t(errorKey))}
      linkedOrganizationId={linkedOrganizationId}
    />
  );
}

export function EditableQuoteCard({
  quote,
  groups,
  isRequestForQuoteClosed,
  requestForQuoteUsesTallyPrice,
  requestForQuoteHasCannedTally,
  requestForQuoteCurrency,
  statusLabel,
}: QuoteCardProps) {
  return (
    <QuoteCardShell
      quote={quote}
      bodyExtra={null}
      rows={
        <QuoteEditForm
          quote={quote}
          groups={groups}
          isRequestForQuoteClosed={isRequestForQuoteClosed}
          requestForQuoteUsesTallyPrice={requestForQuoteUsesTallyPrice}
          requestForQuoteHasCannedTally={requestForQuoteHasCannedTally}
          requestForQuoteCurrency={requestForQuoteCurrency}
        />
      }
      statusLabel={statusLabel}
    />
  );
}
